/**
 * The assistant's pending proposals as an inbox source (lib/inbox): apply or dismiss right in
 * the inbox — the same endpoints as the proposal card (RULES §AI-3).
 */
import { Check, X } from 'lucide-react';
import { api, unwrap } from '@/lib/api/client';
import type { ProposalCard } from '@/lib/api/types';
import { msg, t } from '@/lib/i18n';
import type { InboxSource } from '@/lib/inbox';
import { Button, toast } from '@/ui';
import { aiKeys, useDecideProposal } from './api';

function ProposalButtons({ id }: { id: string }) {
  const decide = useDecideProposal();
  const run = (decision: 'apply' | 'reject') =>
    decide.mutate(
      { id, decision },
      {
        onSuccess: (card) =>
          card.result?.error
            ? toast({ title: t('Not applied'), description: card.result.error, tone: 'danger' })
            : toast({ title: card.result?.message || (decision === 'apply' ? t('Applied') : t('Dismissed')), tone: 'success' }),
        onError: (err) => toast({ title: t('Not applied'), description: err instanceof Error ? err.message : String(err), tone: 'danger' }),
      },
    );
  return (
    <>
      <Button
        variant="primary"
        size="sm"
        icon={Check}
        loading={decide.isPending && decide.variables?.decision === 'apply'}
        disabled={decide.isPending}
        onClick={() => run('apply')}
      >
        {t('Apply')}
      </Button>
      <Button variant="ghost" size="sm" icon={X} disabled={decide.isPending} onClick={() => run('reject')}>
        {t('Dismiss')}
      </Button>
    </>
  );
}

export const proposalsInbox: InboxSource<ProposalCard[]> = {
  key: 'proposals',
  label: msg('Proposals'),
  order: 30,
  permission: 'core.ai.use',
  bell: true,
  query: () => ({ queryKey: aiKeys.proposals, queryFn: () => unwrap(api.GET('/api/ai/proposals')), refetchInterval: 60_000 }),
  items: (list) =>
    list
      .filter((p) => p.status === 'pending')
      .map((p) => ({
        id: `proposal:${p.id}`,
        source: 'proposals',
        kind: msg('Proposal'),
        tone: p.risk === 'high' ? 'bad' : p.risk === 'medium' ? 'medium' : 'info',
        title: p.title,
        detail: p.rationale,
        meta: [p.label],
        at: p.created_at,
        rank: 3,
        actions: <ProposalButtons id={p.id} />,
      })),
};
